import React, {useEffect, useState} from "react";
import {Box, FormControlLabel, FormHelperText, Grid, InputLabel, Radio, RadioGroup, TextField, Typography} from "@mui/material";
import {PowerStationParams} from "../../../../Shared/Types/PowerStation";
import {isValid, startOfDay} from "date-fns";
import {LocalDateTimeFormatISO} from "../../../../Utils/DateFormatPatternUtils";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {validateNumber} from "../../../../Utils/RegexUtils";
import {CustomTextField} from "../../../../Shared/Components/CustomTextField";
import {NumericFormat} from "react-number-format";
import {PowerStationInjectedEnergyLossParameters} from "./PowerStationInjectedEnergyLossParameters";
import {InjectedEnergyLossUpdateItem} from "../../../../Shared/Types/InjectedEnergyLossParameter";
import {
    getDataQualityThresholdError,
    getIntervalError,
    parseIntervalText,
    parseParamText,
    toParamText
} from "./PowerStationParamsValidation";
import {PowerStationParamField} from "./PowerStationParamField";

interface Props {
    onSaveParams: any,
    data: PowerStationParams;
    powerStationId: number | null;
    canEdit: boolean;
    showErrors?: boolean;
    onInjectedEnergyLossChange: (parameters: InjectedEnergyLossUpdateItem[], hasInvalidValues: boolean) => void;
}
export const PowerStationFormParamsPage = ({
    onSaveParams,
    data,
    powerStationId,
    canEdit,
    showErrors = false,
    onInjectedEnergyLossChange
}: Props) => {
    const [powerCapacityNominal, setPowerCapacityNominal] = useState<any>(data?.powerCapacityNominal);
    const [maxPower, setMaxPower] = useState<any>(data?.maxPower );
    const [expectedGenerateEnergy, setExpectedGenerateEnergy] = useState<any>(data?.expectedGenerateEnergy );
    const [expectedYield, setExpectedYield] = useState<any>(data?.expectedYield );
    const [expectedPerformanceRatio, setExpectedPerformanceRatio] = useState<any>(data?.expectedPerformanceRatio );
    const [expectedIrradiance, setExpectedIrradiance] = useState<any>(data?.expectedIrradiance );
    const [operationStartedAt, setOperationStartedAt] = useState<any>(data?.operationStartedAt );
    const [operationMode, setOperationMode] = useState<any>(data?.operationMode);
    const [meterInterval, setMeterInterval] = useState<string>(toParamText(data?.meterInterval));
    const [inverterInterval, setInverterInterval] = useState<string>(toParamText(data?.inverterInterval));
    const [solarimetricInterval, setSolarimetricInterval] = useState<string>(toParamText(data?.solarimetricInterval));
    const [dataQualityThreshold, setDataQualityThreshold] = useState<string>(toParamText(data?.dataQualityThreshold));
    const [meterConst, setMeterConst] = useState<any>(data?.meterConst );
    const [moduleTempCoef, setModuleTempCoef] = useState<any>(data?.moduleTempCoef );
    const [powerStationParamsData, setPowerStationParamsData] = useState<PowerStationParams>(data);

    useEffect(() => {
        onSaveParams(powerStationParamsData)
    }, [powerStationParamsData]);

    useEffect(() => {
        setPowerStationParamsData({
            ...powerStationParamsData,
            operationStartedAt: operationStartedAt
        })
    }, [operationStartedAt]);

    const handleOperationModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setOperationMode((event.target as HTMLInputElement).value);
        setPowerStationParamsData({
            ...powerStationParamsData,
            operationMode: (event.target as HTMLInputElement).value
        })
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        const { id, value } = e.target;
        setPowerStationParamsData({
            ...powerStationParamsData,
            [id]: validateNumber(value) ? Number(value) : 0
        });
    };

    const handleParamTextChange = (
        id: keyof Pick<PowerStationParams, "meterInterval" | "inverterInterval" | "solarimetricInterval" | "dataQualityThreshold">,
        setText: (value: string) => void,
        parse: (text: string) => number | null
    ) => (text: string) => {
        setText(text);
        setPowerStationParamsData(previous => ({
            ...previous,
            [id]: parse(text)
        }));
    };

    const validateField = (data:any) => {
        if(data === ""){
            return (
                <FormHelperText id="my-helper-text">Campo Obrigatório.</FormHelperText>
            )
        }
        if(!validateNumber(data)){
            return (
                <FormHelperText id="my-helper-text">Valor inválido.</FormHelperText>
            )
        }
    }



    return (
        <Grid container spacing={2} sx={{paddingLeft: 2}}>
            <Grid item xs={12} sm={4}>
                <Box style={{marginBottom: 20, paddingTop: 20}}>
                    <InputLabel id="moduleTempCoef">Coeficiente de temperatura do módulo</InputLabel>
                    <NumericFormat
                        id="moduleTempCoef"
                        value={moduleTempCoef}
                        customInput={CustomTextField}
                        onChange={(e) => {
                            setModuleTempCoef(e.target.value);
                            handleInputChange(e);
                        }}
                        placeholder={"Digite o coeficiente de temperatura do modulo"}
                        onFocus={(e) => e.target.setSelectionRange(e.target.value.length, e.target.value.length)}
                        allowNegative={true}
                    />
                    {validateField(moduleTempCoef)}
                </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
                <Box style={{marginBottom: 20, paddingTop: 20}}>
                    <InputLabel id="powerCapacityNominal">Capacidade Instalada</InputLabel>
                    <NumericFormat
                        id="powerCapacityNominal"
                        value={powerCapacityNominal}
                        customInput={CustomTextField}
                        onChange={(e) => {
                            setPowerCapacityNominal(e.target.value);
                            handleInputChange(e)
                        }}
                        placeholder={"Digite a capacidade instalada"}
                        onFocus={(e) => e.target.setSelectionRange(e.target.value.length, e.target.value.length)}
                        allowNegative={true}
                    />
                    {validateField(powerCapacityNominal)}
                </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
                <Box style={{marginBottom: 20, paddingTop: 20}}>
                    <InputLabel id="numberOfUG"  style={{marginBottom: 5}}>Numero de skids</InputLabel>
                    <InputLabel id="numberOfUG">{data?.numberOfUG}</InputLabel>
                </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
                <Box style={{marginBottom: 20}}>
                    <InputLabel id="maxPower">Potência nominal máxima</InputLabel>
                    <NumericFormat
                        id="maxPower"
                        value={maxPower}
                        customInput={CustomTextField}
                        onChange={(e) => {
                            setMaxPower(e.target.value);
                            handleInputChange(e);
                        }}
                        placeholder={"Digite a potencia máxima"}
                        onFocus={(e) => e.target.setSelectionRange(e.target.value.length, e.target.value.length)}
                        allowNegative={true}
                    />
                    {validateField(maxPower)}
                </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
                <Box style={{marginBottom: 20}}>
                    <InputLabel id="expectedGenerateEnergy">Produção de energia estimada</InputLabel>
                    <NumericFormat
                        id="expectedGenerateEnergy"
                        value={expectedGenerateEnergy}
                        customInput={CustomTextField}
                        onChange={(e) => {
                            setExpectedGenerateEnergy(e.target.value);
                            handleInputChange(e);
                        }}
                        placeholder={"Digite a Produção de energia estimada"}
                        onFocus={(e) => e.target.setSelectionRange(e.target.value.length, e.target.value.length)}
                        allowNegative={true}
                    />
                    {validateField(expectedGenerateEnergy)}
                </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
                <Box style={{marginBottom: 20}}>
                    <InputLabel id="expectedYield">Produtividade esperada</InputLabel>
                    <NumericFormat
                        id="expectedYield"
                        value={expectedYield}
                        customInput={CustomTextField}
                        onChange={(e) => {
                            setExpectedYield(e.target.value);
                            handleInputChange(e);
                        }}
                        placeholder={"Digite a Produção de energia estimada"}
                        onFocus={(e) => e.target.setSelectionRange(e.target.value.length, e.target.value.length)}
                        allowNegative={true}
                    />
                    {validateField(expectedYield)}
                </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
                <Box style={{marginBottom: 20}}>
                    <InputLabel id="expectedPerformanceRatio">Taxa de desempenho esperada</InputLabel>
                    <NumericFormat
                        id="expectedPerformanceRatio"
                        value={expectedPerformanceRatio}
                        customInput={CustomTextField}
                        onChange={(e) => {
                            setExpectedPerformanceRatio(e.target.value);
                            handleInputChange(e);
                        }}
                        placeholder={"Digite a taxa de desempenho esperada"}
                        onFocus={(e) => e.target.setSelectionRange(e.target.value.length, e.target.value.length)}
                        allowNegative={true}
                    />
                    {validateField(expectedPerformanceRatio)}
                </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
                <Box style={{marginBottom: 20}}>
                    <InputLabel id="expectedIrradiance">Taxa de irradiação esperada</InputLabel>
                    <NumericFormat
                        id="expectedIrradiance"
                        value={expectedIrradiance}
                        customInput={CustomTextField}
                        onChange={(e) => {
                            setExpectedIrradiance(e.target.value);
                            handleInputChange(e);
                        }}
                        placeholder={"Digite a irradiancia esperada"}
                        onFocus={(e) => e.target.setSelectionRange(e.target.value.length, e.target.value.length)}
                        allowNegative={true}
                    />
                    {validateField(expectedIrradiance)}
                </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
                <Box style={{marginBottom: 20}}>
                    <InputLabel id="operationStartedAt">Início de operação</InputLabel>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            renderInput={(params) => <TextField variant="standard" {...params} />}
                            value={operationStartedAt}
                            inputFormat={"DD/MM/YYYY"}
                            onChange={(newValue) => {
                                if (newValue && isValid(new Date(newValue))) {
                                    setOperationStartedAt(startOfDay(new Date(LocalDateTimeFormatISO(newValue))));
                                }
                            }}
                        />
                    </LocalizationProvider>
                </Box>
            </Grid>
            <Grid item xs={12} sm={12}>
                <Typography sx={{ color: 'text.secondary' }}>Modo de Operação</Typography>
            </Grid>
            <Grid item xs={12} sm={12}>
                <RadioGroup
                    row
                    name="operationMode"
                    value={operationMode}
                    onChange={handleOperationModeChange}
                >
                    <FormControlLabel value="L" control={<Radio />} label="Local" />
                    <FormControlLabel value="R" control={<Radio />} label="Remota" />
                </RadioGroup>
            </Grid>
            <Grid item xs={12} sm={4}>
                <PowerStationParamField
                    id="solarimetricInterval"
                    label="Frequência de Coleta da Solarimétrica [min]"
                    placeholder="Digite a frequência"
                    inputMode="numeric"
                    showErrors={showErrors}
                    value={solarimetricInterval}
                    error={getIntervalError(solarimetricInterval, false)}
                    onChange={handleParamTextChange("solarimetricInterval", setSolarimetricInterval, parseIntervalText)}
                />
            </Grid>
            <Grid item xs={12} sm={4}>
                <PowerStationParamField
                    id="meterInterval"
                    label="Intervalo para coleta de dados do medidor [min]"
                    placeholder="Digite o intervalo"
                    inputMode="numeric"
                    showErrors={showErrors}
                    value={meterInterval}
                    error={getIntervalError(meterInterval, true)}
                    onChange={handleParamTextChange("meterInterval", setMeterInterval, parseIntervalText)}
                />
            </Grid>
            <Grid item xs={12} sm={4}>
                <PowerStationParamField
                    id="inverterInterval"
                    label="Intervalo para coleta de dados do inversor [min]"
                    placeholder="Digite o intervalo"
                    inputMode="numeric"
                    showErrors={showErrors}
                    value={inverterInterval}
                    error={getIntervalError(inverterInterval, true)}
                    onChange={handleParamTextChange("inverterInterval", setInverterInterval, parseIntervalText)}
                />
            </Grid>
            <Grid item xs={12} sm={4}>
                <PowerStationParamField
                    id="dataQualityThreshold"
                    label="Limiar de Qualidade de Dados [%]"
                    placeholder="Digite o limiar"
                    inputMode="decimal"
                    showErrors={showErrors}
                    value={dataQualityThreshold}
                    error={getDataQualityThresholdError(dataQualityThreshold)}
                    onChange={handleParamTextChange("dataQualityThreshold", setDataQualityThreshold, parseParamText)}
                />
            </Grid>
            {/*<Grid item xs={12} sm={4}>*/}
            {/*    <Box style={{marginBottom: 20}}>*/}
            {/*        <InputLabel id="meterConst">Constante do medidor</InputLabel>*/}
            {/*        <NumericFormat*/}
            {/*            id="meterConst"*/}
            {/*            value={meterConst}*/}
            {/*            customInput={CustomTextField}*/}
            {/*            onChange={(e) => {*/}
            {/*                setMeterConst(e.target.value);*/}
            {/*                handleInputChange(e);*/}
            {/*            }}*/}
            {/*            placeholder={"Digite a constante do medidor"}*/}
            {/*            onFocus={(e) => e.target.setSelectionRange(e.target.value.length, e.target.value.length)}*/}
            {/*            allowNegative={true}*/}
            {/*        />*/}
            {/*        {validateField(meterConst)}*/}
            {/*    </Box>*/}
            {/*</Grid>*/}
            <Grid item xs={12}>
                <PowerStationInjectedEnergyLossParameters
                    powerStationId={powerStationId}
                    canEdit={canEdit}
                    onParametersChange={onInjectedEnergyLossChange}
                />
            </Grid>
        </Grid>
    )
}